import { ChartCard } from "../../ChartCard";
import { MetricsChart } from "../../MetricsChart";
import { metricCardState } from "../../../../lib/metricPanelState";
import type { TenantMetrics } from "../../../../hooks/useAdminStatus";

type MetricKind = "cpu" | "mem" | "swap" | "disk";

type MetricPanelProps = {
  readonly title: string;
  readonly metric: MetricKind;
  readonly status: "loading" | "ready" | "error";
  readonly metrics: TenantMetrics | null;
  readonly error: string | null;
};

// swap only started being collected on 2026-08-21, so a tenant that has not
// reported since then has genuinely never had a datapoint rather than a gap in
// the window. "Collecting data" is the honest empty state for that; every other
// metric has full history.
const emptyTextFor = (metric: MetricKind): string =>
  metric === "swap" ? "Collecting data…" : "No data in this window.";

// Only mem/disk have a capacity total. cpu is a pure percentage, and swap is
// sized by a swapfile our own role creates rather than by the instance type, so
// there is no EC2-side figure to report against it.
const totalGibFor = (metric: MetricKind, metrics: TenantMetrics): number | null =>
  metric === "mem" ? metrics.memTotalGib : metric === "disk" ? metrics.diskTotalGib : null;

// Swap is the one metric here with a level that is meaningful on its own:
// healthy is ~0, and sustained use is the leading indicator of the memory
// exhaustion that wedged sunny-ipa64g for ~14 h on 2026-08-19 (swap 97% while
// memory read a comfortable 56%). Fleet baseline sits at 0-15%, so 50% is
// unambiguously wrong without being so close to fatal that the mark arrives too
// late to act on. cpu/mem/disk have no equivalent level - 60% of any of them is
// an ordinary working box - so they pass null and render unchanged.
const SWAP_DANGER_PERCENT = 50;

const thresholdFor = (metric: MetricKind): number | null =>
  metric === "swap" ? SWAP_DANGER_PERCENT : null;

export const MetricPanel = ({ title, metric, status, metrics, error }: MetricPanelProps): JSX.Element => {
  const series = metrics?.series.find((s) => s.metric === metric) ?? null;
  return (
    <ChartCard
      title={title}
      state={metricCardState(status, series)}
      emptyText={emptyTextFor(metric)}
      errorText={error ?? undefined}
    >
      {series != null && metrics != null && (
        <MetricsChart
          series={series}
          bucketSeconds={metrics.bucketSeconds}
          totalGib={totalGibFor(metric, metrics)}
          threshold={thresholdFor(metric)}
        />
      )}
    </ChartCard>
  );
};
